import type { parametres as Fr } from '../fr/parametres';

/** « parametres » in English — typed on the French: a missing sentence does not compile. */
export const parametres: typeof Fr = {
  titre: 'Settings',
  reserve: 'Reserved for the owner',
  lectureSeule: 'Read only',
  onglets: { agence: 'Agency', regles: 'Agreement rules', supplements: 'Extra charges', cgl: 'Terms and conditions' },
  enregistre: 'Saved.',
  enregistreRecharger: 'Saved. Language, currency and formats will apply the next time the page is reloaded.',
  erreur: 'Error',
  suppMisAJour: (l: string) => `“${l}” updated.`,
  arManquantTitre: (n: number) => `${n} clause(s) without Arabic text`,
  arManquantTexte: 'The printed agreement already comes out in two columns, but the Arabic body texts must be pasted from your existing document.',
  lesCompleter: 'Complete them',
  intl: {
    titre: 'Country, language & formats',
    pays: 'Agency country',
    paysAide: 'Drives the suggested legal identifiers and wording (wilayas in Algeria).',
    locale: 'Default language & formats',
    localeAide: 'Interface language for anyone who has not chosen one, and the date and number format.',
    fuseau: 'Time zone',
    fuseauAide: 'Cash register days and due dates follow this time.',
    fuseauFiltre: 'Filter time zones…',
    devise: 'Reference currency',
    deviseAide: 'Three-letter ISO code (DZD, EUR, GBP, USD, CAD, AUD…).',
    unite: 'Distance unit',
    uniteKm: 'Kilometres (km)',
    uniteMi: 'Miles (mi)',
    nomTaxe: 'Tax name',
    nomTaxeAide: 'Printed on invoices: TVA, VAT, GST, Sales tax, IVA, MwSt…',
    tauxTaxe: 'Tax rate (%)',
    taxeParDefaut: 'Tick the tax box by default on new rentals',
    langueDocs: 'Document language',
    langueDocsAide: 'Main language of printed agreements and invoices.',
    langueDocs2: 'Second document language',
    langueDocs2Aide: 'Printed in a second column. “None” for single-language documents.',
    aucune: 'None',
    langues: { fr: 'French', en: 'English', ar: 'Arabic' },
    identifiants: 'Legal identifiers printed on documents',
    identifiantsAide: 'Up to 8 lines. Empty list: documents print the Algerian fields (RC, NIF, NIS, tax article) from the Agency tab.',
    libelle: 'Label',
    valeur: 'Value',
    ajouterIdentifiant: 'Add an identifier',
    retirer: 'Remove',
    preremplir: (pays: string) => `Prefill for ${pays}`,
    remplacerIdentifiants: 'Replace the identifiers already entered with this country’s?',
    rechargement: 'Language, currency and formats are read at startup: they apply the next time the page is reloaded.',
    enregistrer: 'Save country, language & formats',
  },
  pays: {
    DZ: 'Algeria', FR: 'France', BE: 'Belgium', CH: 'Switzerland', CA: 'Canada', GB: 'United Kingdom',
    IE: 'Ireland', US: 'United States', AU: 'Australia', NZ: 'New Zealand', MA: 'Morocco',
    TN: 'Tunisia', ES: 'Spain', PT: 'Portugal',
  },
  locales: {
    'fr-DZ': 'French (Algeria)', 'fr-FR': 'French (France)', 'fr-BE': 'French (Belgium)',
    'fr-CH': 'French (Switzerland)', 'fr-CA': 'French (Canada)', 'fr-MA': 'French (Morocco)', 'fr-TN': 'French (Tunisia)',
    'en-GB': 'English (United Kingdom)', 'en-IE': 'English (Ireland)', 'en-US': 'English (United States)',
    'en-AU': 'English (Australia)', 'en-CA': 'English (Canada)', 'en-NZ': 'English (New Zealand)',
  },
  identite: 'Identity printed on your documents',
  champs: {
    raisonSociale: 'Company name', gerant: 'Manager', rc: 'Trade register (RC)',
    nif: 'Tax ID number (NIF)', nis: 'Statistical ID number (NIS)',
    articleImposition: 'Tax article', telephone: 'Phone', email: 'Email',
    adresse: 'Address', siteWeb: 'Website',
  },
  tauxTva: 'VAT rate (%)',
  tauxTvaAide: '19% in Algeria. The rate is frozen on each invoice when it is issued: changing this figure never rewrites an invoice already issued.',
  article01: 'Clause 01 — age and eligibility',
  ageMinimum: 'Minimum age',
  ageMinimumAide: 'Blocks the booking below this age.',
  anciennetePermis: 'Years holding a driving licence',
  rayon: (u: string) => `Allowed radius (${u})`,
  wilayas: 'Allowed regions',
  regions: 'Allowed regions',
  article02: 'Clause 02 — hours and seasons',
  ouverture: 'Opening',
  fermeture: 'Closing',
  hauteDebut: 'High season — start (MM-DD)',
  hauteDebutAide: 'Summer. Each vehicle’s high-season rate applies.',
  hauteFin: 'High season — end (MM-DD)',
  preparation: 'Preparation time (min)',
  preparationAide: 'A vehicle returned at 11:00 is not available at 11:00.',
  enregistrerRegles: 'Save rules',
  suppSansPrixTitre: (n: number) => `${n} extra charge(s) without a price`,
  suppSansPrixTexte: '— these prices are not in your agreement; enter them here so they get charged.',
  catalogue: 'Single catalogue — the baby seat and late return live in the same list',
  colonnes: { supplement: 'Extra charge', arabe: 'Arabic', unite: 'Unit', moment: 'When', article: 'Clause', prix: 'Price' },
  suppNote: 'Extra charges marked “auto” are suggested automatically at return, with their formula. Changing a price here never alters an agreement already drawn up.',
  cglTitre: 'These texts are printed on the back of the agreement',
  cglTexte1: 'Left column in the primary language, right column in the second language. Arabic titles are already entered;',
  cglTexte2: ' the body texts must be pasted from your existing agreement',
  cglTexte3: ' — we do not write Arabic legal text on your behalf.',
  cglTexteIntl: (l1: string, l2: string) => l2
    ? `Left column: ${l1}. Right column: ${l2}. Paste your own terms — we do not write legal text for you.`
    : `One column: ${l1}. Paste your own terms — we do not write legal text for you.`,
  francais: 'French',
  arabe: 'Arabic',
  languePrincipale: (l: string) => `Primary document language (${l})`,
  langueSeconde: (l: string) => `Second document language (${l})`,
  arabeManquant: 'Arabic text missing — paste it from your agreement',
  geoloc: 'GPS tracking notice',
  aColler: 'Paste from your agreement',
  enregistrerCgl: 'Save terms and conditions',
  moment: { depart: 'Check-out', retour: 'Return' },
  parUnite: (u: string) => `per ${u}`,
  art: (a: string) => `cl. ${a}`,
  auto: 'auto',
};
